import { z } from "zod";
/**
 * Per-project automations: "when X happens (and the task matches), do Y".
 *
 * The shapes are declared once here so the server validates against them and
 * the settings builder in the web app is typed from the same source. The
 * evaluator that reads them is in the API (lib/automation-engine.ts).
 */
export const AUTOMATION_TASK_STATUSES = ["todo", "in_progress", "review", "done", "cancelled"];
export const AUTOMATION_STATUS_LABEL = {
    todo: "To do",
    in_progress: "In progress",
    review: "In review",
    done: "Done",
    cancelled: "Cancelled",
};
/** Narrows the tasks a trigger applies to. Every field given must hold. */
export const automationConditionSchema = z
    .object({
    tagId: z.string().uuid().optional(),
    sectionId: z.string().uuid().optional(),
})
    .strict();
const condition = { condition: automationConditionSchema.optional() };
export const automationTriggerSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("task_created"), ...condition }).strict(),
    /** `to` omitted means "any status". */
    z.object({ type: z.literal("status_changed"), to: z.enum(AUTOMATION_TASK_STATUSES).optional(), ...condition }).strict(),
    /** `userId` omitted means "anyone". */
    z.object({ type: z.literal("assignee_added"), userId: z.string().uuid().optional(), ...condition }).strict(),
    z.object({ type: z.literal("due_approaching"), days: z.number().int().min(1).max(30), ...condition }).strict(),
    z.object({ type: z.literal("task_overdue"), ...condition }).strict(),
]);
const actionUnion = z.discriminatedUnion("type", [
    z
        .object({
        type: z.literal("notify"),
        to: z.enum(["assignees", "owner", "users"]),
        userIds: z.array(z.string().uuid()).max(10).optional(),
        message: z.string().trim().min(1).max(500),
    })
        .strict(),
    z.object({ type: z.literal("set_status"), status: z.enum(AUTOMATION_TASK_STATUSES) }).strict(),
    z.object({ type: z.literal("move_column"), columnId: z.string().uuid() }).strict(),
    z.object({ type: z.literal("add_assignee"), userId: z.string().uuid() }).strict(),
    /** The due date becomes now plus this many days. */
    z.object({ type: z.literal("set_due"), days: z.number().int().min(0).max(365) }).strict(),
    z.object({ type: z.literal("add_tag"), tagId: z.string().uuid() }).strict(),
]);
export const automationActionSchema = actionUnion.refine((a) => a.type !== "notify" || a.to !== "users" || (a.userIds?.length ?? 0) > 0, { message: "Pick at least one person to notify." });
export const automationBodySchema = z.object({
    name: z.string().trim().min(1).max(120),
    enabled: z.boolean().default(true),
    trigger: automationTriggerSchema,
    actions: z.array(automationActionSchema).min(1).max(5),
});
export const AUTOMATION_RUN_OUTCOMES = ["ok", "skipped", "error"];
/**
 * Fill {task}, {project} and {status} in a notification message. Anything else
 * in braces is left as typed, so a stray "{foo}" is visible rather than eaten.
 */
export function renderAutomationMessage(template, vars) {
    return template.replace(/\{(task|project|status)\}/g, (_, key) => vars[key]);
}
//# sourceMappingURL=automations.js.map